import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

// Shared easing: Clay's --ease-out, cubic-bezier(0.19, 1, 0.22, 1)
export const EASE_OUT = [0.19, 1, 0.22, 1];

const viewport = { once: true, margin: "-10% 0px" };

/**
 * Word-by-word reveal. Each word slides up out of a clipped mask with a
 * staggered delay, like Clay's "LineByLine" split headings.
 */
export const SplitReveal = ({ text, as: Tag = "span", className = "", delay = 0, stagger = 0.035 }) => {
    const words = text.split(" ");
    // The in-view check runs on the mask, not the sliding word: a word that
    // starts translated outside its overflow-hidden mask never "intersects".
    const wordVariants = {
        hidden: { y: "110%" },
        visible: (index) => ({
            y: 0,
            transition: { duration: 1, delay: delay + index * stagger, ease: EASE_OUT },
        }),
    };
    return (
        <Tag className={className} aria-label={text}>
            {words.map((word, index) => (
                <motion.span
                    key={`${word}-${index}`}
                    className="word-mask"
                    aria-hidden="true"
                    initial="hidden"
                    whileInView="visible"
                    viewport={viewport}
                >
                    <motion.span className="inline-block" variants={wordVariants} custom={index}>
                        {word}
                    </motion.span>
                    {index < words.length - 1 ? " " : ""}
                </motion.span>
            ))}
        </Tag>
    );
};

/** Fade-up for blocks of content. */
export const Reveal = ({ children, className = "", delay = 0, y = 32 }) => (
    <motion.div
        initial={{ opacity: 0, y }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={viewport}
        transition={{ duration: 1, delay, ease: EASE_OUT }}
        className={className}
    >
        {children}
    </motion.div>
);

/** Media reveal: clip from the bottom while the content settles from a slight zoom. */
export const MediaReveal = ({ children, className = "", delay = 0 }) => (
    <motion.div
        initial={{ clipPath: "inset(12% 0 0 0)", opacity: 0 }}
        whileInView={{ clipPath: "inset(0 0 0 0)", opacity: 1 }}
        viewport={viewport}
        transition={{ duration: 1.2, delay, ease: EASE_OUT }}
        className={`overflow-hidden ${className}`}
    >
        <motion.div
            initial={{ scale: 1.08 }}
            whileInView={{ scale: 1 }}
            viewport={viewport}
            transition={{ duration: 1.4, delay, ease: EASE_OUT }}
            className="h-full w-full"
        >
            {children}
        </motion.div>
    </motion.div>
);

export const ArrowIcon = ({ className = "" }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const ChevronIcon = ({ className = "" }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

/**
 * Accordion row: a title with a rotating chevron, expanding to its body.
 * Used for the services list and the FAQ, like Clay's dropdowns.
 */
export const AccordionItem = ({ title, children, dark = false, titleClassName = "" }) => {
    const [open, setOpen] = useState(false);
    const border = dark ? "border-ink-800" : "border-ink-100";
    const chevron = dark ? "text-ink-400" : "text-ink-300";
    return (
        <div className={`border-b ${border}`}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className={`flex w-full items-center justify-between gap-6 py-6 text-left ${dark ? "text-white" : "text-ink"} ${titleClassName}`}
            >
                <span>{title}</span>
                <motion.span
                    animate={{ rotate: open ? 180 : 0 }}
                    transition={{ duration: 0.5, ease: EASE_OUT }}
                    className={`shrink-0 ${chevron}`}
                >
                    <ChevronIcon />
                </motion.span>
            </button>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        key="body"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.6, ease: EASE_OUT }}
                        className="overflow-hidden"
                    >
                        <div className={`pb-8 t-body max-w-[720px] ${dark ? "text-ink-300" : "text-ink-600"}`}>
                            {children}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
